/**
 * metricTreeLayout — dependency-free auto-layout for the Metric Tree canvas.
 * Arranges the formula decomposition left-to-right: the north-star (a node no
 * formula points INTO) sits on the left, its inputs fan out to the right, columns
 * by depth. Parents are centred on the mean Y of their children so the tree reads
 * like Mixpanel's canvas. Correlation-only / isolated nodes land in column 0.
 *
 * Operates on React Flow nodes/edges (node.id, edge.source/target). Only edges of
 * kind 'formula' (source=parent formula node, target=input child) shape the tree.
 */
const COL_GAP = 360; // horizontal distance between depth columns (card ~230 wide)
const ROW_GAP = 210; // vertical distance between sibling cards (card ~150 tall)
const X0 = 60;
const Y0 = 60;

const isFormulaEdge = (e) => e?.type === 'operator' || (e?.data?.kind || e?.kind) === 'formula';

export function layoutTree(nodes, edges) {
  if (!nodes.length) return nodes;

  // parent → [children] and child → [parents] over formula edges only.
  const childrenOf = new Map();
  const parentsOf = new Map();
  nodes.forEach((n) => {
    childrenOf.set(n.id, []);
    parentsOf.set(n.id, []);
  });
  edges.filter(isFormulaEdge).forEach((e) => {
    if (childrenOf.has(e.source) && childrenOf.has(e.target)) {
      childrenOf.get(e.source).push(e.target);
      parentsOf.get(e.target).push(e.source);
    }
  });

  // depth = longest path from any root (a node with no formula parent). DFS with a
  // visiting guard so an accidental cycle resolves to 0 instead of looping.
  const depth = new Map();
  const state = new Map(); // undefined | 'visiting' | 'done'
  const dfs = (id) => {
    if (state.get(id) === 'done') return depth.get(id);
    if (state.get(id) === 'visiting') return 0; // back-edge — break the cycle
    state.set(id, 'visiting');
    let d = 0;
    (parentsOf.get(id) || []).forEach((p) => {
      d = Math.max(d, dfs(p) + 1);
    });
    depth.set(id, d);
    state.set(id, 'done');
    return d;
  };
  nodes.forEach((n) => dfs(n.id));

  // group node ids by column (depth), preserving input order for stable stacking.
  const cols = new Map();
  nodes.forEach((n) => {
    const d = depth.get(n.id) || 0;
    if (!cols.has(d)) cols.set(d, []);
    cols.get(d).push(n.id);
  });

  // initial positions: even vertical spacing within each column.
  const pos = new Map();
  const depths = [...cols.keys()].sort((a, b) => a - b);
  depths.forEach((d) => {
    cols.get(d).forEach((id, i) => {
      pos.set(id, { x: X0 + d * COL_GAP, y: Y0 + i * ROW_GAP });
    });
  });

  // second pass (deepest column inward): centre each parent on its children's mean Y.
  const maxD = depths[depths.length - 1] || 0;
  for (let d = maxD - 1; d >= 0; d -= 1) {
    (cols.get(d) || []).forEach((id) => {
      const kids = childrenOf.get(id) || [];
      if (kids.length) {
        const ys = kids.map((k) => pos.get(k).y);
        pos.get(id).y = ys.reduce((a, b) => a + b, 0) / ys.length;
      }
    });
  }

  // third pass: within every column, push cards apart so centring never overlaps
  // them (keep at least ROW_GAP between consecutive cards, preserving order).
  depths.forEach((d) => {
    const ids = [...(cols.get(d) || [])].sort((a, b) => pos.get(a).y - pos.get(b).y);
    for (let i = 1; i < ids.length; i += 1) {
      const prev = pos.get(ids[i - 1]).y;
      const cur = pos.get(ids[i]);
      if (cur.y - prev < ROW_GAP) cur.y = prev + ROW_GAP;
    }
  });

  return nodes.map((n) => ({ ...n, position: { x: pos.get(n.id).x, y: pos.get(n.id).y } }));
}

// Card footprint used by the tiered layout to guarantee non-overlap.
const CARD_W = 230;
const CARD_H = 170;

/**
 * layoutTiered — clean row/column grid for HIERARCHY trees (the KPI Tree template:
 * a root with 3 Level-2 / 9 Level-3 / 9 Bet children). Unlike layoutTree (which
 * only shapes formula/operator edges) this arranges an arbitrary parent→child
 * hierarchy carried by ordinary connector edges, so Tidy on a wide KPI tree lands
 * in tiers instead of collapsing into a radial spiral (BUG-MT-06).
 *
 * opts.orientation 'vertical' (default here) stacks tiers top-down with siblings
 * spread horizontally; 'horizontal' lays tiers left-to-right. opts.parentIsTarget
 * picks which end of an edge is the parent — KPI connectors run child→parent
 * (source=child, target=parent), so parent = edge.target (the default). Nodes not
 * touched by any hierarchy edge (e.g. the Level-band text labels) keep their place.
 */
export function layoutTiered(nodes, edges, opts = {}) {
  if (!nodes.length) return nodes;
  const vertical = opts.orientation !== 'horizontal';
  const parentIsTarget = opts.parentIsTarget !== false;

  const LEVEL_GAP = vertical ? CARD_H + 90 : CARD_W + 130; // 260 / 360
  const CROSS_GAP = vertical ? CARD_W + 50 : CARD_H + 60; // 280 / 230
  const ORIGIN = 60;

  const has = new Set(nodes.map((n) => n.id));
  const parentsOf = new Map();
  nodes.forEach((n) => parentsOf.set(n.id, []));
  const connected = new Set();
  edges.forEach((e) => {
    if (!has.has(e.source) || !has.has(e.target)) return;
    const parent = parentIsTarget ? e.target : e.source;
    const child = parentIsTarget ? e.source : e.target;
    parentsOf.get(child).push(parent);
    connected.add(e.source);
    connected.add(e.target);
  });

  // level(node) = max(level(parent)) + 1; roots = 0. Bounded relaxation so an
  // accidental cycle can't loop forever.
  const level = new Map();
  nodes.forEach((n) => level.set(n.id, 0));
  for (let pass = 0; pass < nodes.length; pass += 1) {
    let changed = false;
    nodes.forEach((n) => {
      let want = 0;
      (parentsOf.get(n.id) || []).forEach((p) => { want = Math.max(want, (level.get(p) || 0) + 1); });
      if (want > (level.get(n.id) || 0)) { level.set(n.id, want); changed = true; }
    });
    if (!changed) break;
  }

  // Group connected nodes by level, preserving input order for stable columns.
  const byLevel = new Map();
  nodes.forEach((n) => {
    if (!connected.has(n.id)) return;
    const l = level.get(n.id) || 0;
    if (!byLevel.has(l)) byLevel.set(l, []);
    byLevel.get(l).push(n.id);
  });
  const levels = [...byLevel.keys()].sort((a, b) => a - b);
  if (!levels.length) return nodes; // nothing connected — leave positions as-is
  const maxCount = Math.max(...levels.map((l) => byLevel.get(l).length));
  const totalCross = maxCount * CROSS_GAP;

  const pos = new Map();
  levels.forEach((l) => {
    const ids = byLevel.get(l);
    const rowCross = ids.length * CROSS_GAP;
    const start = ORIGIN + (totalCross - rowCross) / 2; // centre each tier
    ids.forEach((id, i) => {
      const cross = start + i * CROSS_GAP;
      const depth = ORIGIN + l * LEVEL_GAP;
      pos.set(id, vertical ? { x: cross, y: depth } : { x: depth, y: cross });
    });
  });

  return nodes.map((n) => (pos.has(n.id) ? { ...n, position: pos.get(n.id) } : n));
}

/**
 * layoutRadial — North Star arrangement: the centre node (a formula/metric that
 * no formula points into, or the first node) sits in the middle; its inputs ring
 * around it on a circle. Used by the North Star Framework template + its Tidy.
 */
export function layoutRadial(nodes, edges) {
  if (!nodes.length) return nodes;
  const referenced = new Set();
  edges.filter(isFormulaEdge).forEach((e) => referenced.add(e.target));
  // Centre = a node that drives others but nothing drives it; else the first node.
  const center = nodes.find((n) => !referenced.has(n.id)
    && edges.filter(isFormulaEdge).some((e) => e.source === n.id)) || nodes[0];

  const CX = 500;
  const CY = 380;
  const R = 360;
  const spokes = nodes.filter((n) => n.id !== center.id);
  const step = (2 * Math.PI) / Math.max(spokes.length, 1);
  const start = -Math.PI / 2; // first spoke at the top

  return nodes.map((n) => {
    if (n.id === center.id) return { ...n, position: { x: CX, y: CY } };
    const i = spokes.findIndex((s) => s.id === n.id);
    const angle = start + i * step;
    return {
      ...n,
      position: { x: Math.round(CX + R * Math.cos(angle)), y: Math.round(CY + R * Math.sin(angle)) },
    };
  });
}

// ── MT-20: duplicate-label detection (pure; shared by the canvas + tests) ──
// Two formula-eligible metrics sharing a label make formula inputs ambiguous and
// the saved tree unreadable. Returns the sorted list of labels used by more than
// one metric node; an empty list means the tree is safe to save. The canvas treats
// a non-empty result as a BLOCKING validation error (the save must not proceed).
export function duplicateMetricLabels(nodes) {
  const counts = {};
  (nodes || []).forEach((n) => {
    if (((n.data && n.data.type) || 'metric') !== 'metric') return; // only formula-eligible nodes
    const label = ((n.data && n.data.label) || '').trim();
    if (label) counts[label] = (counts[label] || 0) + 1;
  });
  return Object.keys(counts).filter((l) => counts[l] > 1).sort((a, b) => a.localeCompare(b));
}

// ── MT-11: overlap detection (pure; shared by the canvas + tests) ──
// Node footprint — measured dimensions when React Flow has them, else a
// per-type default, so the overlap test reflects the real cards.
export function nodeSize(n) {
  if (n.width && n.height) return { w: n.width, h: n.height };
  const t = (n.data && n.data.type) || n.type || '';
  if (String(t).includes('note')) return { w: 200, h: 96 };
  if (String(t).includes('text')) return { w: 220, h: 60 };
  return { w: 232, h: 150 };
}

// Does any pair of nodes overlap by more than a small tolerance? Used to skip
// auto-layout on an already-clean tree (never degrade a good layout).
export function hasNodeOverlaps(nodes) {
  const list = (nodes || []).filter((n) => n && n.position);
  for (let i = 0; i < list.length; i += 1) {
    const a = list[i]; const as = nodeSize(a);
    for (let j = i + 1; j < list.length; j += 1) {
      const b = list[j]; const bs = nodeSize(b);
      const ix = Math.min(a.position.x + as.w, b.position.x + bs.w) - Math.max(a.position.x, b.position.x);
      const iy = Math.min(a.position.y + as.h, b.position.y + bs.h) - Math.max(a.position.y, b.position.y);
      if (ix > 12 && iy > 12) return true; // meaningful overlap in BOTH axes
    }
  }
  return false;
}

// BUG-MT-11: COUNT of meaningfully-overlapping node pairs (both axes, >12px).
// Tidy uses this as a regression guard — if a layout would produce MORE overlaps
// than the input had, it is discarded and the canvas left untouched.
export function countNodeOverlaps(nodes) {
  const list = (nodes || []).filter((n) => n && n.position);
  let count = 0;
  for (let i = 0; i < list.length; i += 1) {
    const a = list[i]; const as = nodeSize(a);
    for (let j = i + 1; j < list.length; j += 1) {
      const b = list[j]; const bs = nodeSize(b);
      const ix = Math.min(a.position.x + as.w, b.position.x + bs.w) - Math.max(a.position.x, b.position.x);
      const iy = Math.min(a.position.y + as.h, b.position.y + bs.h) - Math.max(a.position.y, b.position.y);
      if (ix > 12 && iy > 12) count += 1;
    }
  }
  return count;
}

// A lane/section header is a presentational label (template "text" node). It
// carries NO edges, so it must be kept OUT of the edge-driven layout pass and
// repositioned afterward relative to the lane it labels (BUG-MT-11).
export function isLaneHeader(n) {
  const t = (n && n.data && n.data.type) || (n && n.type) || '';
  return String(t) === 'text';
}

/**
 * layoutWithLanes — BUG-MT-11. The edge-driven layouts (layoutTiered/Tree/Radial)
 * treat an unconnected node as a stray graph node: a lane/section header (which has
 * no edges) gets packed into the flow or stranded far from the row it labels, and
 * ends up overlapping the repositioned cards. This orchestrator fixes that:
 *
 *   1. Partition presentational headers out; lay out ONLY the graph nodes.
 *   2. Reserve a band on the cross axis (left for a vertical/top-down tree, top for
 *      a horizontal one) by shifting the graph so the header band is clear.
 *   3. Second pass: place each header at the band's leading edge, aligned to the
 *      flow-axis centre of the lane it owns (the graph nodes that originally shared
 *      its lane), so the label stays attached to its row/column on every topology.
 *
 * Pure. `base` is the branch-selected layout fn already bound to its edges by the
 * caller: base(nodes) → laid-out nodes (only the graph subset is ever passed in).
 */
export function layoutWithLanes(nodes, base, orientation) {
  const headers = (nodes || []).filter(isLaneHeader);
  const graph = (nodes || []).filter((n) => !isLaneHeader(n));
  if (!headers.length || !graph.length) return base(nodes); // nothing to lane — plain layout

  const vertical = orientation === 'vertical';
  const flow = vertical ? 'y' : 'x'; // axis tiers advance along
  const cross = vertical ? 'x' : 'y'; // axis the lane band sits on
  const HEADER_ORIGIN = 20; // leading-edge position of the label band
  const BAND = vertical ? 320 : 150; // reserved band width (x) / height (y)
  const LANE_TOL = 120; // a graph node belongs to a header's lane if within this on `flow`

  // 1) lay out the graph nodes (headers excluded).
  let laidRaw = base(graph);

  // Preserve the template's intended flow direction. The edge-driven layout roots
  // at whichever end the edges point to, which can MIRROR the author's arrangement
  // (e.g. a Bets→…→Outputs left-to-right North Star comes back Outputs-first). If
  // the new flow ordering is inversely correlated with the original, flip the flow
  // axis so a left-to-right template stays left-to-right (and top-down stays top).
  const origFlow = new Map(graph.map((n) => [n.id, n.position ? (n.position[flow] || 0) : 0]));
  const newFlowById = new Map(laidRaw.map((n) => [n.id, n.position[flow] || 0]));
  const ids = graph.map((n) => n.id);
  const mean = (arr) => arr.reduce((a, b) => a + b, 0) / (arr.length || 1);
  const mo = mean(ids.map((id) => origFlow.get(id)));
  const mn = mean(ids.map((id) => newFlowById.get(id)));
  const cov = ids.reduce((s, id) => s + (origFlow.get(id) - mo) * (newFlowById.get(id) - mn), 0);
  if (cov < 0) {
    const flows = ids.map((id) => newFlowById.get(id));
    const lo = Math.min(...flows); const hi = Math.max(...flows);
    laidRaw = laidRaw.map((n) => ({ ...n, position: { ...n.position, [flow]: lo + hi - (n.position[flow] || 0) } }));
  }

  // 2) shift the graph past the reserved header band on the cross axis.
  const laid = laidRaw.map((n) => ({
    ...n,
    position: { ...n.position, [cross]: (n.position[cross] || 0) + BAND },
  }));
  const laidById = new Map(laid.map((n) => [n.id, n]));

  // 3) place each header at the band's leading edge, centred on its lane's new flow.
  const origFlowById = new Map(graph.map((n) => [n.id, n.position ? (n.position[flow] || 0) : 0]));
  const placedHeaders = headers.map((h) => {
    const hFlow = h.position ? (h.position[flow] || 0) : 0;
    const laneIds = graph
      .filter((g) => Math.abs((origFlowById.get(g.id)) - hFlow) <= LANE_TOL)
      .map((g) => g.id);
    if (!laneIds.length) return h; // header owns no discernible lane — leave it be
    const flows = laneIds.map((id) => laidById.get(id).position[flow]);
    const centreFlow = flows.reduce((a, b) => a + b, 0) / flows.length;
    return { ...h, position: { [flow]: centreFlow, [cross]: HEADER_ORIGIN } };
  });

  // Return in the ORIGINAL node order (React Flow keeps selection/refs stable).
  const byId = new Map([...laid, ...placedHeaders].map((n) => [n.id, n]));
  return nodes.map((n) => byId.get(n.id) || n);
}
